/** @jsxImportSource react */
'use client';

import { useEffect, useState } from 'react';

import Link from 'next/link';

import { AnimatePresence, motion } from 'motion/react';

import { track } from './analytics';
import { OnArkLogo } from './brand';
import { Close, Menu } from './icons';
import { SignupLink } from './signup-link';

const LINKS = [
  { href: '/features', label: 'Features' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/planning-center', label: 'Planning Center' },
  { href: '/customers', label: 'Customers' },
];

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll when the mobile menu is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [open]);

  return (
    <>
      <nav
        className={[
          'sticky top-0 z-50 transition-[background-color,border-color,box-shadow,backdrop-filter] duration-300',
          scrolled
            ? 'border-b border-[color:var(--color-border-soft)] bg-white/90 shadow-[0_1px_24px_-12px_rgba(15,11,26,0.18)] backdrop-blur-xl'
            : 'border-b border-transparent bg-white/60 backdrop-blur-sm',
        ].join(' ')}
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4 px-5 py-4 sm:px-8 sm:py-5 lg:px-20 lg:py-7">
          <Link
            aria-label="OnArk home"
            className="shrink-0 transition-transform hover:scale-[1.02]"
            href="/"
          >
            <OnArkLogo className="h-7 w-auto sm:h-[37px]" />
          </Link>
          <div className="hidden items-center gap-10 lg:flex">
            {LINKS.map(link => (
              <Link
                key={link.href}
                className="text-[15px] font-medium text-[color:var(--color-ink)] transition-colors hover:text-[color:var(--color-amethyst)]"
                href={link.href}
              >
                {link.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Link
              className="hidden text-[15px] font-medium text-[color:var(--color-ink)] transition-colors hover:text-[color:var(--color-amethyst)] sm:inline-block"
              href="/login"
            >
              Log in
            </Link>
            <SignupLink
              className="rounded-[10px] bg-[color:var(--color-ink)] px-4 py-2.5 text-[14px] font-medium text-white transition-transform hover:scale-[1.02] sm:px-5 sm:py-3 sm:text-[15px]"
              source="nav"
            >
              Try for free
            </SignupLink>
            <button
              aria-expanded={open}
              aria-label="Open menu"
              className="grid h-10 w-10 place-items-center rounded-[10px] text-[color:var(--color-ink)] transition-colors hover:bg-[color:var(--color-bg-mist)] lg:hidden"
              type="button"
              onClick={() => {
                setOpen(true);
                track('mobile_menu_opened');
              }}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-[60] lg:hidden"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* Backdrop */}
            <button
              aria-label="Close menu"
              className="absolute inset-0 bg-[color:var(--color-ink)]/40"
              type="button"
              onClick={() => setOpen(false)}
            />

            {/* Panel */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="absolute inset-x-0 top-0 flex flex-col gap-8 rounded-b-[20px] bg-white p-6 shadow-[0_24px_60px_-20px_rgba(15,11,26,0.25)] sm:p-8"
              exit={{ opacity: 0, y: -8 }}
              initial={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-center justify-between gap-4">
                <OnArkLogo className="h-7 w-auto" />
                <button
                  aria-label="Close menu"
                  className="grid h-10 w-10 place-items-center rounded-[10px] text-[color:var(--color-ink)] transition-colors hover:bg-[color:var(--color-bg-mist)]"
                  type="button"
                  onClick={() => setOpen(false)}
                >
                  <Close className="h-5 w-5" />
                </button>
              </div>
              <ul className="flex flex-col gap-1">
                {LINKS.map(link => (
                  <li key={link.href}>
                    <Link
                      className="block rounded-[10px] py-3.5 text-[22px] font-medium tracking-[-0.01em] text-[color:var(--color-ink)] transition-colors hover:text-[color:var(--color-amethyst)]"
                      href={link.href}
                      onClick={() => setOpen(false)}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col gap-3 border-t border-[color:var(--color-border-soft)] pt-6">
                <Link
                  className="rounded-[12px] border border-[color:var(--color-border-soft)] px-5 py-3.5 text-center text-[15px] font-medium text-[color:var(--color-ink)] transition-colors hover:bg-[color:var(--color-bg-mist)]"
                  href="/login"
                  onClick={() => setOpen(false)}
                >
                  Log in
                </Link>
                <SignupLink
                  className="rounded-[12px] bg-[color:var(--color-amethyst)] px-5 py-3.5 text-center text-[15px] font-semibold text-white transition-transform hover:scale-[1.01]"
                  source="nav-mobile"
                  onClick={() => setOpen(false)}
                >
                  Try OnArk free
                </SignupLink>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
